import { createAsyncThunk } from "@reduxjs/toolkit";

import apiRequest from "../../../services/api";
import { setApiValue } from "../../../utils/shared/apiValues";

// Program filters
export const fetchProgramFilters = createAsyncThunk(
  "programs/fetch_filters",
  async (body) => {
    let query = ``;
    if (body.search) {
      query = query + `search=${body.search}&`;
    }
    const programsFilters = await apiRequest.get(
      `v2/programs/filters/?${query}`,
      {
        headers: { organization: body.userOrganization?.org_id },
      }
    );
    return programsFilters.data;
  }
);
export const fetchProgramCategoryFilters = createAsyncThunk(
  "programs/fetch_category_filters",
  async (body) => {
    let query = `page=${body.page ? body.page : 1}&`;
    if (body.page_size) {
      query = query + `page_size=${body.page_size}&`;
    }
    if (body.learningType) {
      query = query + `learning_type=${body.learningType}&`;
    }
    if (body.search) {
      query = query + `search=${body.search}&`;
    }
    const programsFilters = await apiRequest.get(
      `v2/programs/learning/filters/?${query}`,
      {
        headers: { organization: body.userOrganization?.org_id },
      }
    );
    return programsFilters.data;
  }
);

export const createQuestionTag = createAsyncThunk(
  "programs/create_question_tag",
  async (body, { rejectWithValue }) => {
    try {
      const response = await apiRequest.post(
        `v2/program/question/tag/`,
        body.data
      );
      return response.data;
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);

export const fetchQuestionTags = createAsyncThunk(
  "programs/fetch_question_tags",
  async (body) => {
    let query = `page=${body.page ? body.page : 1}&`;
    if (body.page_size) {
      query = query + `page_size=${body.page_size}&`;
    }
    if (body.learningType) {
      query = query + `learning_type=${body.learningType}&`;
    }
    if (body.search) {
      query = query + `search=${body.search}&`;
    }
    const questionTags = await apiRequest.get(
      `v2/program/question/tag/?${query}`,
      {
        headers: { organization: body.userOrganization.org_id },
      }
    );
    return questionTags.data;
  }
);

export const fetchProgramLearningPointFilters = createAsyncThunk(
  "programs/fetch_learning_point_filters",
  async (body) => {
    let query = `page=${body.page ? body.page : 1}&`;
    if (body.page_size) {
      query = query + `page_size=${body.page_size}&`;
    }
    if (body.learningType) {
      query = query + `learning_type=${body.learningType}&`;
    }
    if (body.search) {
      query = query + `search=${body.search}&`;
    }
    const programsFilters = await apiRequest.get(
      `v2/programs/learning/filters/?${query}`,
      {
        headers: { organization: body.userOrganization?.org_id },
      }
    );
    return programsFilters.data;
  }
);

// Program questions
export const fetchAllProgramsQuestions = createAsyncThunk(
  "programs/fetch_all_questions",
  async (body) => {
    let query = `page=${body.page ? body.page : 1}&`;
    if (body.page_size) {
      query = query + `page_size=${body.page_size}&`;
    }
    if (body.ordering) {
      query = query + `ordering=${body.ordering}&`;
    }
    if (body.type) {
      query = query + `type=${body.type}&`;
    }
    if (body.status) {
      query = query + `status=${body.status}&`;
    } else {
      query = query + `status=INUSE,READY&`;
    }
    if (body.modules) {
      query = query + `module_type=${body.modules}&`;
    }
    if (body.roles) {
      query = query + `role=${body.roles}&`;
    }
    if (body.category) {
      query = query + `category=${body.category}&`;
    }
    if (body.learningPoint) {
      query = query + `learning_point=${body.learningPoint}&`;
    }
    if (body.search) {
      query = query + `search=${body.search}&`;
    }
    if (body.questionTags) {
      query = query + `question_tags=${body.questionTags}`;
    }
    const programsAllQuestions = await apiRequest.get(
      `v2/programs/questions/?${query}`,
      {
        headers: { organization: body.userOrganization?.org_id },
      }
    );
    return programsAllQuestions.data;
  }
);

// Program recommended questions
export const fetchProgramsRecommendedQuestions = createAsyncThunk(
  "programs/fetch_all_recommended_questions",
  async (body) => {
    let query = `page=${body.page ? body.page : 1}&`;
    if (body.page_size) {
      query = query + `page_size=${body.page_size}&`;
    }
    if (body.type) {
      query = query + `type=${body.type}&`;
    }
    if (body.modules) {
      query = query + `module_type=${body.modules}&`;
    }
    if (body.roles) {
      query = query + `role=${body.roles}&`;
    }
    if (body.category) {
      query = query + `category=${body.category}&`;
    }
    if (body.status) {
      query = query + `status=${body.status}&`;
    } else {
      query = query + `status=INUSE,READY&`;
    }
    if (body.learningPoint) {
      query = query + `learning_point=${body.learningPoint}&`;
    }
    if (body.search) {
      query = query + `search=${body.search}`;
    }
    const programsRecomQuestions = await apiRequest.get(
      `v2/programs/questions/?${query}`,
      {
        headers: { organization: body.userOrganization?.org_id },
      }
    );
    return programsRecomQuestions.data;
  }
);

// Program question details
export const fetchProgramQuesDetails = createAsyncThunk(
  "programs/fetch_program_ques_details",
  async (body) => {
    const programsFilters = await apiRequest.get(
      `v2/programs/questions/detail/?question_id=` + body.questionId,
      {
        headers: { organization: body.userOrganization?.org_id },
      }
    );
    return programsFilters.data;
  }
);

export const updateQuestionStatus = createAsyncThunk(
  "programs/update_question_status",
  async (body, { rejectWithValue }) => {
    try {
      const response = await apiRequest.patch(
        `v1/program/questions/${body.questionId}/`,
        body.data,
        {
          headers: { organization: body.userOrganization?.org_id },
        }
      );
      return response.data;
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);

// fetch Question Analytics
export const fetchQuestionAnalytics = createAsyncThunk(
  "programs/fetch_question_analytics",
  async (body, { rejectWithValue }) => {
    try {
      const analytics = await apiRequest.get(
        `v1/question/${body.questionId}/analytics/`,
        {
          headers: { organization: body.userOrganization?.org_id },
        }
      );
      return analytics.data;
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);

// Program modules list
export const fetchAllProgramsModules = createAsyncThunk(
  "programs/fetch_all_modules",
  async (body) => {
    let query = `page=${body.page ? body.page : 1}&`;
    if (body.page_size) {
      query = query + `page_size=${body.page_size}&`;
    }
    if (body.module_type) {
      query = query + `module_type=${body.module_type}&`;
    }
    // if (body.modules) {
    //   query = query + `module_type=${body.modules}&`;
    // }
    if (body.roles) {
      query = query + `module_role=${body.roles}&`;
    }
    if (body.category) {
      query = query + `module_categories=${body.category}&`;
    }
    if (body.learningPoint) {
      query = query + `learning_point=${body.learningPoint}&`;
    }
    if (body.internal_notes) {
      query = query + `internal_notes=${body.internal_notes}&`;
    }
    if (body.search) {
      query = query + `search=${body.search}&`;
    }
    if (body.ordering) {
      query = query + `ordering=${body.ordering}`;
    }
    if (body.status) {
      query = query + `is_active=${body.status}`;
    }
    if (body.media__post_type) {
      query = query + `media__post_type=${body.media__post_type}`;
    }
    const programsModules = await apiRequest.get(
      `v2/programs/modules/?${query}`,
      {
        headers: { organization: body.userOrganization?.org_id },
      }
    );
    return programsModules.data;
  }
);

// Program module details
export const fetchAllOrganization = createAsyncThunk(
  "programs/organizations/list",
  async (body) => {
    let query = `page=${body.page ? body.page : 1}&`;
    if (body.page_size) {
      query = query + `page_size=${body.page_size}&`;
    }
    if (body.search) {
      query = query + `search=${body.search}&`;
    }
    if (body.orderby) {
      query = query + `ordering=${body.orderby}&`;
    }
    if (body.programVisibleToOrgs) {
      query = query + `org_id__in=${body.programVisibleToOrgs}&`;
    }
    const allOrganizations = await apiRequest.get(
      `v2/programs/organizations/?${query}`,
      {
        headers: { organization: body.userOrganization?.org_id },
      }
    );
    return allOrganizations.data;
  }
);

// Program module details
export const fetchProgramModuleDetails = createAsyncThunk(
  "programs/fetch_program_module_details",
  async (body) => {
    const programsFilters = await apiRequest.get(
      `v2/programs/modules/?module_id=` + body.moduleId,
      {
        headers: { organization: body.userOrganization?.org_id },
      }
    );
    return programsFilters.data;
  }
);
// Program modules create
export const createNewProgramModule = createAsyncThunk(
  "programs/create_module",
  async (body) => {
    const modulesData = await apiRequest.post(
      `v2/programs/modules/`,
      body.newModuleData,
      { headers: { organization: body.userOrganization?.org_id } }
    );
    return modulesData;
  }
);

// Program modules update
export const updateNewProgramModule = createAsyncThunk(
  "programs/update_module",
  async (body) => {
    try {
      const modulesData = await apiRequest.put(
        `v2/programs/modules/?module_id=${body.moduleId}`,
        body.newModuleData,
        {
          headers: { organization: body.userOrganization?.org_id },
        }
      );
      return modulesData;
    } catch (error) {
      if (error.response?.data) {
        return error.response.data;
      }
      console.log(error);
      throw error;
    }
  }
);
export const deleteProgramModule = createAsyncThunk(
  "programs/delete_program_module",
  async (body, { rejectWithValue }) => {
    try {
      const programModuleDelete = await apiRequest.delete(
        `v2/programs/modules/?module_id=` + body.moduleId,
        {
          headers: { organization: body.userOrganization?.org_id },
        }
      );
      return programModuleDelete.data;
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);
// Program module duplicate
export const duplicateProgramModule = createAsyncThunk(
  "programs/duplicate_program_module",
  async (body) => {
    const programModuleDuplicate = await apiRequest.post(
      `v2/programs/modules/duplicate/?module_id=` + body.moduleId,
      {
        headers: { organization: body.userOrganization?.org_id },
      }
    );
    return programModuleDuplicate;
  }
);

// All Programs
export const fetchAllPrograms = createAsyncThunk(
  "programs/fetch_all_programs",
  async (body) => {
    let query = `page=${body.page ? body.page : 1}`;
    if (body.page_size) {
      query = query + `&page_size=${body.page_size}`;
    }
    if (body.complexity) {
      query = query + `&complexity=${body.complexity}`;
    }
    if (body.roles) {
      query = query + `&role=${body.roles}`;
    }
    if (body.category) {
      query = query + `&categories=${body.category}`;
    }
    if (body.learningPoint) {
      query = query + `&learning_points=${body.learningPoint}`;
    }
    if (body.search) {
      query = query + `&search=${body.search}`;
    }
    if (body.ordering) {
      query = query + `&ordering=${body.ordering}`;
    }
    if (body.internal_notes) {
      query = query + `&internal_notes=${body.internal_notes}`;
    }
    if (body.program_type) {
      query = query + `&internal_program_type=${body.program_type}`;
    }
    if (body.status) {
      query = query + `&status=${body.status}`;
    }
    if (body.program_id) {
      query = query + `&program_id=${body.program_id}`;
    }
    const programsList = await apiRequest.get(`v2/programs/?${query}`, {
      headers: { organization: body.userOrganization?.org_id },
    });
    return programsList.data;
  }
);

// Create Program
export const createNewProgram = createAsyncThunk(
  "programs/create_new_program",
  async (body, { rejectWithValue }) => {
    try {
      const createProgramApi = await apiRequest.post(
        `v2/programs/create/`,
        body.newProgramData,
        {
          headers: {
            organization: body.userOrganization?.org_id,
            "content-type": "multipart/form-data",
          },
        }
      );
      return createProgramApi.data;
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);

// Program update
export const updateNewProgram = createAsyncThunk(
  "programs/update_program",
  async (body, { rejectWithValue }) => {
    try {
      const programData = await apiRequest.put(
        `v2/programs/?program_id=${body.programId}`,
        body.updatedProgramData,
        {
          headers: {
            organization: body.userOrganization?.org_id,
            "content-type": "multipart/form-data",
          },
        }
      );
      return programData;
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);

export const getProgramHandouts = createAsyncThunk(
  "programs/get_program_handouts",
  async (body) => {
    const handouts = await apiRequest.get(
      `v2/programs/${body.programId}/handouts/`,
      {
        headers: {
          organization: body.userOrganization?.org_id,
        },
      }
    );
    return handouts.data;
  }
);

export const createProgramHandouts = createAsyncThunk(
  "programs/create_program_handouts",
  async (body) => {
    const handouts = await apiRequest.post(
      `v2/programs/${body.programId}/handouts/`,
      body.handoutsData,
      {
        headers: {
          organization: body.userOrganization?.org_id,
          "content-type": "multipart/form-data",
        },
      }
    );
    return handouts.data;
  }
);

export const updateProgramHandouts = createAsyncThunk(
  "programs/update_program_handouts",
  async (body) => {
    const handouts = await apiRequest.patch(
      `v2/programs/${body.programId}/handouts/`,
      body.handoutsData,
      {
        headers: { organization: body.userOrganization?.org_id },
      }
    );
    return handouts.data;
  }
);

// Handouts deletion
export const deleteProgramHandouts = createAsyncThunk(
  "programs/delete_handouts",
  async (body, { rejectWithValue }) => {
    try {
      const response = await apiRequest.delete(
        `v2/programs/${body.programId}/handouts/`,
        { data: body.handoutsData }
      );
      return response.data;
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);

// Program details
export const fetchProgramDetails = createAsyncThunk(
  "programs/fetch_program_details",
  async (body) => {
    const programsDetails = await apiRequest.get(
      `v2/programs/?program_id=` + body.programId,
      {
        headers: { organization: body.userOrganization?.org_id },
      }
    );
    return programsDetails.data;
  }
);

// Program chapters
export const fetchProgramChapters = createAsyncThunk(
  "programs/fetch_program_chapters",
  async (body) => {
    const programsChapters = await apiRequest.get(
      `v2/programs/chapters/?program_id=` + body.programId,
      {
        headers: { organization: body.userOrganization?.org_id },
      }
    );
    return programsChapters.data;
  }
);

// Create Program Chapters
export const createProgramChapters = createAsyncThunk(
  "programs/create_program_chapters",
  async (body) => {
    const programChaptersApi = await apiRequest.post(
      `v2/programs/chapters/bulk_create/`,
      body.newProgramChaptersData,
      {
        headers: { organization: body.userOrganization?.org_id },
      }
    );
    return programChaptersApi.data;
  }
);

// Bulk Update Program Chapters
export const updateProgramChapters = createAsyncThunk(
  "programs/update_program_chapters",
  async (body) => {
    const programChaptersApi = await apiRequest.post(
      `v2/programs/chapters/bulk_update/`,
      body.existingProgramChaptersData,
      {
        headers: { organization: body.userOrganization?.org_id },
      }
    );
    return programChaptersApi.data;
  }
);

// Delete Program Chapter
export const deleteProgramChapter = createAsyncThunk(
  "programs/delete_program_chapters",
  async (body, { rejectWithValue }) => {
    try {
      const programChapterDeleteApi = await apiRequest.delete(
        `v2/programs/chapters/?program_id=` +
          body.programId +
          `&module_id=` +
          body.moduleId,
        {
          headers: { organization: body.userOrganization?.org_id },
        }
      );
      return programChapterDeleteApi.data;
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);

// Program chapter update
export const updateProgramChapter = createAsyncThunk(
  "programs/update_program_chapter",
  async (body) => {
    const programData = await apiRequest.put(
      `v2/programs/chapters/?program_id=` +
        body.programId +
        `&module_id=` +
        body.moduleId,
      body.updatedChapterData,
      {
        headers: { organization: body.userOrganization?.org_id },
      }
    );
    return programData;
  }
);

// Program update
export const updateProgram = createAsyncThunk(
  "programs/update_program",
  async (body) => {
    const programData = await apiRequest.put(
      `v2/programs/?program_id=` + body.programId,
      body.newModuleData,
      {
        headers: { organization: body.userOrganization?.org_id },
      }
    );
    return programData;
  }
);

// Program delete
export const deleteProgram = createAsyncThunk(
  "programs/delete_program",
  async (body, { rejectWithValue }) => {
    try {
      const programDelete = await apiRequest.delete(
        `v2/programs/?program_id=` + body.programId,
        {
          headers: { organization: body.userOrganization?.org_id },
        }
      );
      return programDelete.data;
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);
// Program duplicate
export const duplicateProgram = createAsyncThunk(
  "programs/duplicate_program",
  async (body) => {
    const programDuplicate = await apiRequest.post(
      `v2/programs/duplicate/?program_id=` + body.programId,
      body.newDuplicateData,
      {
        headers: { organization: body.userOrganization?.org_id },
      }
    );
    return programDuplicate;
  }
);

// Upload program questions bulk upload file
export const uploadBulkUploadFile = createAsyncThunk(
  "programs/question_bulk_upload",
  async (body, { rejectWithValue }) => {
    try {
      const newQuesBulkUpload = await apiRequest.post(
        `v1/questions/bulk-upload/`,
        body.newQuesBulkUploadData,
        {
          headers: {
            organization: body.userOrganization?.org_id,
            "content-type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const totalLength = progressEvent.lengthComputable
              ? progressEvent.total
              : progressEvent.target.getResponseHeader("content-length") ||
                progressEvent.target.getResponseHeader(
                  "x-decompressed-content-length"
                );
            const uploadProgress = Math.round(
              (progressEvent.loaded * 100) / totalLength
            );
            if (uploadProgress !== null) {
              setApiValue({
                type: "quesUploadWindowProgress",
                data: uploadProgress,
              });
            }
          },
        }
      );
      return newQuesBulkUpload.data;
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);

// Bulk upload review
export const fetchProgramBulkUploadReview = createAsyncThunk(
  "programs/fetch_program_bulk_upload_review",
  async () => {
    const programBulkUploadReview = await apiRequest.get(
      "v1/questions/bulk-upload/review/"
    );
    return programBulkUploadReview.data;
  }
);

// Bulk upload review - Edit question
export const fetchProgramBulkUploadReviewQuestion = createAsyncThunk(
  "programs/fetch_program_bulk_upload_review_ques",
  async (body) => {
    const programBulkUploadReviewQues = await apiRequest.get(
      `v1/question/${body.QuestionId}/`
    );
    return programBulkUploadReviewQues.data;
  }
);

// Bulk upload - Delete staged question
export const deleteStagedQuestion = createAsyncThunk(
  "programs/delete_staged_question",
  async (body, { rejectWithValue }) => {
    try {
      const deleteStagedApi = await apiRequest.delete(
        `v1/question/${body.question_id}/`
      );
      return deleteStagedApi.data;
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);

// Bulk upload - Discard staged question
export const discardStagedQuestion = createAsyncThunk(
  "programs/discard_staged_question",
  async (body, { rejectWithValue }) => {
    try {
      const discardStagedApi = await apiRequest.delete(
        "v1/questions/bulk-upload/"
      );
      return discardStagedApi.data;
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);

// Program staged ques update
export const updateStagedQuestion = createAsyncThunk(
  "programs/update_staged_question",
  async (body, { rejectWithValue }) => {
    try {
      const updateData = await apiRequest.put(
        `v1/question/${body.questionId}/`,
        body.updatedQuestionData
      );
      return updateData;
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);

// Program staged ques publish
export const publishStagedQuestion = createAsyncThunk(
  "programs/publish_staged_question",
  async (body, { rejectWithValue }) => {
    try {
      const publishData = await apiRequest.post(`v1/questions/publish/`);
      return publishData;
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);

// update program question
export const updateProgramQuestion = createAsyncThunk(
  "programs/update_program_question",
  async (body, { rejectWithValue }) => {
    try {
      const programData = await apiRequest.put(
        `v1/program/questions/${body.questionId}/`,
        body.updatedQuestionData,
        {
          headers: { organization: body.userOrganization?.org_id },
        }
      );
      return programData;
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);

// Delete uploaded program question
export const deleteProgramQuestion = createAsyncThunk(
  "programs/delete_existing_question",
  async (body, { rejectWithValue }) => {
    try {
      const deleteQuesApi = await apiRequest.post(
        `v1/program/questions/delete/`,
        body.questionData
      );
      return deleteQuesApi.data;
    } catch (err) {
      if (!err.response) {
        throw err;
      }
      return rejectWithValue(err.response.data);
    }
  }
);

// program type filter list
export const fetchProgramTypeFilters = createAsyncThunk(
  "programs/fetch_program_type_filters",
  async (body) => {
    let query = `page=${body.page ? body.page : 1}`;
    if (body.page_size) {
      query = query + `&page_size=${body.page_size}`;
    }
    if (body.search) {
      query = query + `&search=${body.search}`;
    }
    if (body.ordering) {
      query = query + `&ordering=${body.ordering}`;
    }
    const programsTypeFilters = await apiRequest.get(
      `v2/programs/filters/program_type/?${query}`,
      {
        headers: { organization: body.userOrganization?.org_id },
      }
    );
    return programsTypeFilters.data;
  }
);

// program internal notes filter

export const fetchProgramOrModuleInternalNotesFilters = createAsyncThunk(
  "programs/fetch_program_internal_notes_filters",
  async (body) => {
    let query = `page=${body.page ? body.page : 1}`;
    if (body.page_size) {
      query = query + `&page_size=${body.page_size}`;
    }
    if (body.search) {
      query = query + `&search=${body.search}`;
    }
    if (body.ordering) {
      query = query + `&ordering=${body.ordering}`;
    }
    if (body.source) {
      query = query + `&source=${body.source}`;
    }
    const programsOrModuleInternalNotesFilters = await apiRequest.get(
      `v2/programs/filters/internal_notes/?${query}`,
      {
        headers: { organization: body.userOrganization?.org_id },
      }
    );
    return programsOrModuleInternalNotesFilters.data;
  }
);

/* Question association view apis */
export const fetchQuestionAssociationPrograms = createAsyncThunk(
  "programs/fetch_question_association_programs",
  async (body) => {
    let query = "";
    if (body.page) {
      query = query + `page=${body.page}&`;
    }
    if (body.page_size) {
      query = query + `page_size=${body.page_size}&`;
    }
    if (body.ordering) {
      query = query + `ordering=${body.ordering}&`;
    }
    const programData = await apiRequest.get(
      `v2/programs/${body.questionId}/analytics/?${query}`,
      { headers: { organization: body.userOrganization?.org_id } }
    );
    return programData.data;
  }
);

export const fetchQuestionAssociationTournaments = createAsyncThunk(
  "contest/fetch_question_association_tournaments",
  async (body) => {
    let query = "";
    if (body.page) {
      query = query + `page=${body.page}&`;
    }
    if (body.page_size) {
      query = query + `page_size=${body.page_size}&`;
    }
    if (body.ordering) {
      query = query + `ordering=${body.ordering}&`;
    }
    const tournamentData = await apiRequest.get(
      `v2/contests/${body.questionId}/analytics/?${query}`,
      { headers: { organization: body.userOrganization?.org_id } }
    );
    return tournamentData.data;
  }
);

export const fetchAllLearningPoints = createAsyncThunk(
  "programs/learningPoints/list",
  async (body, { rejectWithValue }) => {
    try {
      let query = "";
      if (body.page) {
        query = query + `page=${body.page}&`;
      }
      if (body.page_size) {
        query = query + `page_size=${body.page_size}&`;
      }
      const allLearningPoints = await apiRequest.get(
        `v2/programs/explore_learning_points/?${query}`,
        {
          headers: { organization: body.userOrganization?.org_id },
        }
      );
      return allLearningPoints.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch learning points"
      );
    }
  }
);
